import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

/**
 * Fallback copy for shared list status primitives' error, denial, and retry chrome.
 *
 * Deliberately generic: every calling surface supplies its own `empty` slot and usually its own
 * `blocked` slot, so this text only appears where a surface has nothing more specific to say.
 */
export const LIST_VIEW_MESSAGES = {
  fallbackError: msg`Something went wrong loading this list.`,
  retry: msg`Try again.`,
  nextPageError: msg`Could not load more items.`,
  loadingMore: msg`Loading more items…`,
  blocked: msg`You do not have access to this list.`,
} as const;

/**
 * Per-status copy for the `failed` arm, keyed by `ApiHttpError.status`.
 *
 * `401` and `403` are deliberately absent: pages classify an authorization denial as
 * `blocked`, so it can never reach the initial `failed` arm. Any status without an entry resolves to
 * {@link LIST_VIEW_MESSAGES.fallbackError}.
 */
export const LIST_VIEW_ERROR_MESSAGES: Record<number, MessageDescriptor> = {
  400: msg`This request could not be processed.`,
  404: msg`This list is no longer available.`,
  408: msg`The request timed out.`,
  429: msg`Too many requests. Wait a moment and try again.`,
  500: msg`The server could not load this list.`,
  502: msg`The server could not load this list.`,
  503: msg`The server could not load this list.`,
  504: msg`The server could not load this list.`,
};
